import { useThdCustomer } from '@thd-olt-functional/customer-information';
import React, { createContext, useEffect, useState, } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FeatureSwitches } from '../constants/feature-switches';
import { GetOrderHistoryData } from '../hooks/purchase-history-hooks';
import { useCommonContext } from '../hooks/useContexts';
import { DateRanges } from '../utils/DateRanges';
import { getWindow } from '../utils/general';
import { isUserAllowedToViewAllPurchases } from '../utils/policy-utils';
import { initLDRequestBuilder, initPHRequestBuilder, } from '../utils/purchase-history';
var defaultState = {
    alertState: null,
    localDeliveryAlertState: null,
    customerRole: '',
    customerType: '',
    cpsDataSource: undefined,
    dateSelectorValue: 'recentOrders',
    initResultsFilter: 'myOrders',
    openAddReceiptDrawer: false,
    orderHistoryData: undefined,
    orderHistoryError: undefined,
    orderHistoryLoaded: false,
    localDeliveryData: undefined,
    localDeliveryError: undefined,
    localDeliveryLoaded: false,
    setLocalDeliveryInfo: function () { return null; },
    pHRequest: {
        pageSize: 20,
        pageNumber: 1,
        startDate: DateRanges.recentOrders.startDate,
        endDate: DateRanges.recentOrders.endDate,
    },
    lDRequest: {
        startDate: DateRanges.recentOrders.startDate,
        endDate: DateRanges.recentOrders.endDate,
        pageNumber: 1,
        pageSize: 20,
    },
    setAlertState: function () { return null; },
    allOrderDetails: [],
    setAllOrderDetails: function () { return null; },
    setLocalDeliveryAlertState: function () { return null; },
    setDateSelectorValue: function () { return null; },
    setOpenAddReceiptDrawer: function () { return null; },
    setPHRequest: function () { return null; },
    setLDRequest: function () { return null; },
    setViewType: function () { return null; },
    viewType: 'Card',
};
// Create the context with a default value
export var PurchaseHistoryContext = createContext(defaultState);
export var PurchaseHistoryProvider = function (_a) {
    var _b, _c, _d, _e, _f;
    var children = _a.children, customerType = _a.customerType, customerRole = _a.customerRole;
    var svocID = useThdCustomer().svocID;
    var searchParams = useSearchParams()[0];
    var timezone = useCommonContext().timezone;
    var initDateSelectorValue = function () {
        var dateSelectorValue = searchParams.get('dateSelectorValue');
        if (FeatureSwitches.enableLSSCPS25MonthsDefault === true &&
            customerType === 'b2b') {
            return 'last25Months';
        }
        if (dateSelectorValue) {
            return dateSelectorValue;
        }
        if (customerType === 'b2b') {
            return 'last6Months';
        }
        return 'recentOrders';
    };
    // Hook used for opening add receipt drawer anywhere on purchase history
    var _g = useState(false), openAddReceiptDrawer = _g[0], setOpenAddReceiptDrawer = _g[1];
    // Hook used for alert message on purchase history page
    var _h = useState(null), alertState = _h[0], setAlertState = _h[1];
    // Hook used for local delivery alert message on purchase history page
    var _j = useState(null), localDeliveryAlertState = _j[0], setLocalDeliveryAlertState = _j[1];
    // Hook used for "View History From" dropdown
    var _k = useState(
    // eslint-disable-next-line no-nested-ternary
    searchParams.get('dateSelectorValue') ||
        initDateSelectorValue()), dateSelectorValue = _k[0], setDateSelectorValue = _k[1];
    // Hook used for storing order details information for each order to be called back
    var _l = useState([]), allOrderDetails = _l[0], setAllOrderDetails = _l[1];
    var _m = useState(''), viewType = _m[0], setViewType = _m[1];
    var _o = useState({
        localDeliveryData: undefined,
        localDeliveryError: undefined,
        localDeliveryLoaded: false,
    }), localDeliveryInfo = _o[0], setLocalDeliveryInfo = _o[1];
    var isMobile = useCommonContext().isMobile;
    var localStorageViewType = (_b = getWindow()) === null || _b === void 0 ? void 0 : _b.localStorage.getItem('viewType');
    useEffect(function () {
        var initialViewType = function () {
            if (isMobile)
                return 'Card';
            if (localStorageViewType)
                return localStorageViewType;
            if (customerType !== 'b2b')
                return 'Card';
            return 'Table';
        };
        setViewType(initialViewType);
    }, [localStorageViewType]);
    // Utility to check to see if the user can select purchases filter
    var initResultsFilter = isUserAllowedToViewAllPurchases(customerRole, customerType)
        ? 'allOrders'
        : 'myOrders';
    var phFilter = (_d = (_c = getWindow()) === null || _c === void 0 ? void 0 : _c.sessionStorage) === null || _d === void 0 ? void 0 : _d.getItem('PHFilter');
    var sessionPHFilter = phFilter ? JSON.parse(phFilter) : undefined;
    var _p = useState(initPHRequestBuilder({
        customerType: customerType,
        customerRole: customerRole,
        dateSelectorValue: (_e = sessionPHFilter === null || sessionPHFilter === void 0 ? void 0 : sessionPHFilter.selectedDate) !== null && _e !== void 0 ? _e : dateSelectorValue,
        searchParams: searchParams,
        svocID: svocID,
        initResultsFilter: (_f = sessionPHFilter === null || sessionPHFilter === void 0 ? void 0 : sessionPHFilter.userView) !== null && _f !== void 0 ? _f : initResultsFilter,
        sessionData: sessionPHFilter,
        timezone: timezone,
    })), pHRequest = _p[0], setPHRequest = _p[1];
    // Hook for getting order/purchase history data
    var _q = GetOrderHistoryData(pHRequest), orderHistoryData = _q.orderHistoryData, orderHistoryError = _q.orderHistoryError, orderHistoryLoaded = _q.orderHistoryLoaded, cpsDataSource = _q.cpsDataSource;
    var _r = useState(initLDRequestBuilder({
        dateSelectorValue: 'last6Months',
        searchParams: searchParams,
        svocID: svocID,
        searchType: 'ORDERS',
        resultsFilter: 'allOrders',
        sortBy: 'salesDate',
        sortOrder: 'DESC',
        groupBy: 'FULLFILLMENT_TYPE',
    })), LDRequest = _r[0], setLDRequest = _r[1];
    return (React.createElement(PurchaseHistoryContext.Provider, { value: {
            alertState: alertState,
            localDeliveryAlertState: localDeliveryAlertState,
            dateSelectorValue: dateSelectorValue,
            initResultsFilter: initResultsFilter,
            openAddReceiptDrawer: openAddReceiptDrawer,
            orderHistoryData: orderHistoryData,
            orderHistoryError: orderHistoryError,
            orderHistoryLoaded: orderHistoryLoaded,
            localDeliveryData: localDeliveryInfo.localDeliveryData,
            localDeliveryError: localDeliveryInfo.localDeliveryError,
            localDeliveryLoaded: localDeliveryInfo.localDeliveryLoaded,
            setLocalDeliveryInfo: setLocalDeliveryInfo,
            pHRequest: pHRequest,
            lDRequest: LDRequest,
            setAlertState: setAlertState,
            allOrderDetails: allOrderDetails,
            setAllOrderDetails: setAllOrderDetails,
            setLocalDeliveryAlertState: setLocalDeliveryAlertState,
            setDateSelectorValue: setDateSelectorValue,
            setOpenAddReceiptDrawer: setOpenAddReceiptDrawer,
            setPHRequest: setPHRequest,
            setLDRequest: setLDRequest,
            setViewType: setViewType,
            viewType: viewType,
            customerType: customerType,
            customerRole: customerRole,
            cpsDataSource: cpsDataSource,
        } }, children));
};
